import React from "react";
import {
  Box,
  Typography,
  CardContent,
  LinearProgress,
  Divider,
  Stack,
} from "@mui/material";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { PurchaseOrderStatsDto } from "../../../shared/dtos/purchaseOrder/PurchaseOrderStatsDto";

interface PurchaseOrderWidgetProps {
  data: PurchaseOrderStatsDto;
}

const PurchaseOrderWidget: React.FC<PurchaseOrderWidgetProps> = ({ data }) => {
  const meta = getEntityMeta("PURCHASE_ORDER");
  const Icon = meta.icon;

  const total = Math.max(0, data.total || 0);

  const getPercentage = (count: number): number =>
    total > 0 ? Math.min(100, Math.max(0, (count / total) * 100)) : 0;

  const statuses = [
    {
      label: "Pending",
      count: data.pending,
      color: "warning" as const,
    },
    {
      label: "Approved",
      count: data.approved,
      color: "success" as const,
    },
    {
      label: "Sent to Supplier",
      count: data.sentToSupplier,
      color: "info" as const,
    },
  ];

  return (
    <StyledCard sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>

          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {meta.title}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Total Purchase Orders
          </Typography>

          <Typography variant="h4" sx={{ fontWeight: "bold" }}>
            {total.toLocaleString()}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2}>
          {statuses.map((status) => (
            <Box key={status.label}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {status.label}
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {status.count.toLocaleString()}
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={getPercentage(status.count)}
                color={status.color}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </StyledCard>
  );
};

export default PurchaseOrderWidget;
